import React, { ReactNode } from "react";
import { IntlShape, useIntl } from "react-intl";
import { msgs } from "../../msgs";
import {
  ChainUpdateEntry,
  ChildInventoryOverride,
  InventoryLinesByChild,
  MultiUpdateChildPromotionsInput,
  PromotionChanges,
} from "../../types";
import { ApprovalCard, CardHeader, Section } from "./shared";
import { isAllInventoryPhrase } from "./formatting";
import { PromotionChangeRows } from "./update-summary";

// Describes which child engines ONE chain-update entry targets — the same "all / all except"
// convention as GeolocationValue's country phrasing (see approvalAllChildren/
// approvalAllChildrenExcept), applied to child engines instead of countries. Every entry has a
// scope (even an implicit "all children" one), so this never returns an empty string.
function describeChainScope(
  entry: Pick<ChainUpdateEntry, "includedChildTickers" | "excludedChildTickers">,
  intl: IntlShape
): string {
  if (entry.includedChildTickers && entry.includedChildTickers.length > 0) {
    return entry.includedChildTickers.join(", ");
  }
  if (entry.excludedChildTickers && entry.excludedChildTickers.length > 0) {
    return intl.formatMessage(msgs.approvalAllChildrenExcept, {
      children: entry.excludedChildTickers.join(", "),
    });
  }
  return intl.formatMessage(msgs.approvalAllChildren);
}

// Renders which engines a chain-update entry's change ACTUALLY reaches — `affectedChildren` is
// server-computed (the promotion was found on each of these) and can be NARROWER than the
// requested scope (describeChainScope), since a chain promotion doesn't necessarily exist yet on
// every targeted child. Falls back to the requested scope (as plain text) when affectedChildren
// isn't present (e.g. older persisted tool calls from before this field existed). Only what IS
// impacted is ever shown here — requested-but-not-found children (missingChildren) are
// intentionally omitted from the approval card; they don't affect anything, so listing them next
// to every row added noise without helping the user review the change.
//
// Always rendered under its own "Motores afectados" title (approvalAffectedEnginesTitle),
// unlike the country list above there is no length threshold: exactly one engine is plain text,
// two or more ALWAYS collapse behind a toggle — a promotion card is meant to be scannable at a
// glance, and even a 2-item inline list competed with the title for attention.
function ChainAffectedEngines({
  entry,
  intl,
}: {
  entry: Pick<
    ChainUpdateEntry,
    "affectedChildren" | "includedChildTickers" | "excludedChildTickers"
  >;
  intl: IntlShape;
}): React.JSX.Element {
  const engines = entry.affectedChildren ?? [];
  const title = intl.formatMessage(msgs.approvalAffectedEnginesTitle);

  if (engines.length <= 1) {
    const text = engines.length === 1 ? engines[0] : describeChainScope(entry, intl);
    return (
      <Section label={title}>
        <span className="text-[#212121] text-sm truncate">{text}</span>
      </Section>
    );
  }

  return (
    <Section label={title}>
      {/* Only shown alongside the collapsible variant — the plain-text 1-engine case above
          already names the engine directly, so this intro would just repeat it. */}
      <span className="text-[#212121] text-sm">
        {intl.formatMessage(msgs.approvalAffectedEnginesIntro, {
          count: engines.length,
          b: (chunks: ReactNode) => <strong className="font-semibold">{chunks}</strong>,
        })}
      </span>
      <details className="group w-full rounded-[10px] border border-[#E5E7EB] bg-white overflow-hidden">
        <summary className="flex h-[43px] items-center justify-between gap-2 px-4 cursor-pointer list-none text-[#212121] text-sm marker:content-none [&::-webkit-details-marker]:hidden">
          {/* Closed-state label: the first engine in the list (not a count — the "Afecta a N
              motores" intro above already covers that), same idea as a <select>'s default. */}
          {engines[0]}
          <em className="fa fa-chevron-down text-[13px] text-[#212121]" aria-hidden />
        </summary>
        {/* engines[0] is already shown in the <summary> above (open or closed, <summary> stays
            visible) — skip it here so expanding the list doesn't repeat the first engine. */}
        <div className="flex flex-col gap-1 max-h-[168px] overflow-y-auto px-4 pb-3">
          {engines.slice(1).map(engine => (
            <span key={engine} className="text-[#212121] text-sm break-words">
              {engine}
            </span>
          ))}
        </div>
      </details>
    </Section>
  );
}

// The union of affected/included/excluded child tickers across every entry sharing ONE ticker —
// used to render a SINGLE "Afecta a…" line per promotion block (see MultiUpdateChainApprovalSummary)
// instead of one per entry. A ticker normally has the same scope across its entries (e.g. a
// visibility change and a rate change both scoped to "all children"), so the union is exact in
// the common case; when entries genuinely target different subsets, the union still correctly
// names every engine reached by AT LEAST ONE of that promotion's changes, which is what "which
// engines does this promotion update touch" means at the promotion level.
function mergedChainScope(
  group: ChainUpdateEntry[]
): Pick<ChainUpdateEntry, "affectedChildren" | "includedChildTickers" | "excludedChildTickers"> {
  const dedupe = (values: (string[] | undefined)[]) =>
    Array.from(new Set(values.flat().filter((v): v is string => v != null)));
  return {
    affectedChildren: dedupe(group.map(e => e.affectedChildren)),
    includedChildTickers: dedupe(group.map(e => e.includedChildTickers)),
    excludedChildTickers: dedupe(group.map(e => e.excludedChildTickers)),
  };
}

/**
 * Merges every entry sharing ONE ticker into a SINGLE synthesized entry, so PromotionChangeRows
 * renders that promotion's shared-field diff EXACTLY ONCE per card instead of once per entry (see
 * MultiUpdateChainApprovalSummary — this is the fix for the duplication bug reported against
 * promotion NICO26: devices/geolocation/group/restrictions/discount/dates were each shown once
 * per affected child engine). This is safe because a chain update is UNIFORM across the whole
 * chain except inventory (see BULK UPDATE FIELD RULES on the backend): the model only splits ONE
 * promotion's change into several entries when DIFFERENT attributes are scoped to DIFFERENT child
 * subsets (e.g. visibility on every child, a rate change on only one of them) — every entry that
 * sets a given `changes` key carries the SAME value for it, so folding every entry's `changes`
 * (and the flat window fields PromotionChangeRows also reads) together, key by key, correctly
 * shows each changed field once regardless of how many entries the request was split into.
 * `inventoryByChild` — the one genuinely per-child field — is unioned by child ticker instead of
 * overwritten, so no affected child's breakdown is lost if it appears on more than one entry.
 */
function mergedChainEntry(group: ChainUpdateEntry[], intl: IntlShape): ChainUpdateEntry {
  const changes: PromotionChanges = {};
  for (const e of group) Object.assign(changes, e.changes ?? {});

  const inventoryByTicker = new Map<
    string,
    NonNullable<ChainUpdateEntry["inventoryByChild"]>[number]
  >();
  for (const e of group) {
    for (const child of e.inventoryByChild ?? []) {
      if (child?.childTicker) inventoryByTicker.set(child.childTicker, child);
    }
  }

  // FALLBACK — reproduced live on NICO26: a chain update split into a demo_eleana entry ("solo
  // adultos") and a demo_toni entry ("cancelación gratuita"), where the model's WRITE call omitted
  // the optional inventoryByChild/childInventoryOverrides breakdown entirely (it had included it on
  // an earlier, rejected retry, but dropped it once the call finally passed validation). With no
  // structured per-child data, the blind `changes` merge above silently kept only the LAST entry's
  // `inventoryScope` (demo_toni's 52 lines), and demo_eleana's 65 disappeared without a trace — the
  // approval card showed one flat "52 líneas" instead of a per-engine breakdown.
  //
  // When NO entry in this group carries a structured breakdown, but more than one entry has a
  // DIFFERENT `changes.inventoryScope.after`, synthesize one from each entry's OWN inventoryScope +
  // its OWN affected children — inventory is the one field this whole merge function otherwise
  // treats as safely overwritable (see the doc comment above), and this is the case where that
  // assumption breaks. An entry whose scope is the "apply to all inventory" phrase is skipped here
  // (there's no fixed per-child count to show for it) rather than showing a wrong number; it's still
  // reflected in `changes.inventoryScope` for whichever entry the blind merge happened to keep.
  if (inventoryByTicker.size === 0) {
    const allInventoryLabel = intl.formatMessage(msgs.approvalAllInventory);
    const distinctScopes = new Set(
      group
        .map(e => e.changes?.inventoryScope?.after)
        .filter((v): v is string => v != null)
        .map(String)
    );
    if (distinctScopes.size > 1) {
      for (const e of group) {
        const scope = e.changes?.inventoryScope;
        if (!scope?.after) continue;
        const scopeValue = String(scope.after);
        if (isAllInventoryPhrase(scopeValue, allInventoryLabel)) continue;
        // Prefer the backend-computed `affectedCount`/`afterCount` (see their doc comments in
        // types.ts) over counting commas in `scopeValue` — that string is now CAPPED at a small
        // fixed size regardless of the real scope (see `describeScope` on the backend), so a
        // comma-count silently undercounts anything past the cap. Only entries that predate both
        // fields fall back to the (now unreliable, but better than nothing) comma count.
        const count =
          scope.affectedCount ??
          scope.afterCount ??
          scopeValue
            .split(",")
            .map(s => s.trim())
            .filter(Boolean).length;
        const children = e.affectedChildren?.length
          ? e.affectedChildren
          : e.includedChildTickers ?? [];
        for (const childTicker of children) {
          if (!inventoryByTicker.has(childTicker)) {
            inventoryByTicker.set(childTicker, { childTicker, lineCount: count });
          }
        }
      }
    }
  }

  const inventoryByChild =
    inventoryByTicker.size > 0 ? Array.from(inventoryByTicker.values()) : undefined;

  // Same "inventory lives in exactly ONE entry" assumption as inventoryByChild above (the DTO
  // actively steers the model toward ONE entry + childInventoryOverrides for per-child divergence
  // — see chainInventoryFields' doc comment on the backend) — picked explicitly, rather than left
  // to the shallow spread below, so it doesn't depend on which entry happens to be LAST in the
  // group. In the rare case more than one entry genuinely carries a selector, the first one found
  // wins; merging two DIFFERENT descriptors field-by-field has no clean answer for `occupancy`
  // (a single fixed combination, not a unionable list), so this doesn't attempt it.
  const inventoryScopeDescriptor = group.find(e => e.inventoryScopeDescriptor)
    ?.inventoryScopeDescriptor;

  // Shallow-merge every OTHER field (window fields, ticker, etc.) — later entries' keys win when
  // present, which is harmless here since PromotionChangeRows never reads the scope fields
  // (affectedChildren/includedChildTickers/excludedChildTickers) this merge leaves un-deduped —
  // those are rendered separately via ChainAffectedEngines(mergedChainScope(group)).
  const merged = group.reduce<ChainUpdateEntry>((acc, e) => ({ ...acc, ...e }), {
    ticker: group[0]?.ticker ?? "",
  });
  return { ...merged, changes, inventoryByChild, inventoryScopeDescriptor };
}

// Builds the per-child inventory breakdown ({childTicker, lineCount}) the dropdown renders,
// keyed by child engine. Precedence, strongest last:
//   1. entries[].inventoryByChild scalar lineCount   (legacy fallback)
//   2. childInventoryOverrides scalar lineCount        (legacy fallback, override-only children)
//   3. inventoryLinesByChild.lines.length              (AUTHORITATIVE — real tickers, count-safe)
// The authoritative source WINS whenever present because the scalars were fabrication-prone; the
// fallbacks only fill in children the authoritative source doesn't cover (e.g. messages persisted
// before it existed). Returns undefined when nothing is present, so a shared-field-only update
// keeps `hasChainInventoryBreakdown` false and shows no dropdown.
function buildPerChildInventory(
  inventoryByChild: ChainUpdateEntry["inventoryByChild"],
  overrides: ChildInventoryOverride[],
  linesByChild: InventoryLinesByChild[]
): ChainUpdateEntry["inventoryByChild"] {
  type Row = {
    childTicker: string;
    lineCount: number;
    mode?: "replace" | "add" | "remove";
    beforeCount?: number;
  };
  const byChild = new Map<string, Row>();
  for (const c of inventoryByChild ?? []) {
    if (c?.childTicker)
      byChild.set(c.childTicker, {
        childTicker: c.childTicker,
        lineCount: c.lineCount ?? 0,
        mode: c.mode,
        beforeCount: c.beforeCount,
      });
  }
  for (const o of overrides) {
    if (o?.childTicker && !byChild.has(o.childTicker)) {
      byChild.set(o.childTicker, {
        childTicker: o.childTicker,
        lineCount: o.lineCount ?? 0,
        mode: o.appliesToBookableMode,
      });
    }
  }
  // `linesByChild` (authoritative — see this function's own doc comment) only carries the REAL
  // matched-line COUNT, never mode/beforeCount — preserve whatever this child already picked up
  // from `inventoryByChild`/`overrides` above instead of dropping it, so a child whose count comes
  // from the authoritative source can still render "Removes N (M left)" instead of losing that
  // context the moment the count source switches.
  for (const l of linesByChild) {
    if (l?.childTicker) {
      const existing = byChild.get(l.childTicker);
      byChild.set(l.childTicker, {
        childTicker: l.childTicker,
        lineCount: Array.isArray(l.lines) ? l.lines.length : 0,
        mode: existing?.mode,
        beforeCount: existing?.beforeCount,
      });
    }
  }
  return byChild.size > 0 ? Array.from(byChild.values()) : undefined;
}

// Groups a flat array of {ticker, ...} items by their `ticker` field, into a Map for O(1)
// per-ticker lookup. Used to pre-index `childInventoryOverrides`/`inventoryLinesByChild` by
// ticker ONCE before the per-group render loop below, instead of re-filtering both full arrays
// for every group (previously `overrides.filter(o => o.ticker === ...)` and
// `linesByChild.filter(l => l.ticker === ...)` inside the `.map()`, an O(groups × items) scan).
function groupByTicker<T extends { ticker?: string }>(items: T[]): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    if (!item.ticker) continue;
    const arr = map.get(item.ticker);
    if (arr) arr.push(item);
    else map.set(item.ticker, [item]);
  }
  return map;
}

/**
 * Renders the before→after diff for a CHAIN multi-entry update (updateMultipleChildPromotions) —
 * SEVERAL DIFFERENT (ticker, child-scope) chain-promotion updates, as ONE SEPARATE ApprovalCard
 * PER TICKER (not per entry, and not one shared card for all of them — see
 * MultiUpdateApprovalSummary above for the same per-card approach on the multi-ticker flow). A
 * ticker can have more than one entry when different attributes were scoped to different child
 * engines (e.g. visibility on all children, a rate change scoped to only one) — those entries are
 * merged into that ticker's SAME card instead of producing a duplicate card for it. Each card ends
 * with the affected engines ONCE (the union across the ticker's entries — see mergedChainScope),
 * instead of repeating an affected-engines line per attribute.
 */
export function MultiUpdateChainApprovalSummary({ input }: { input: unknown }) {
  const intl = useIntl();
  const data = (input ?? {}) as MultiUpdateChildPromotionsInput;
  const entries = (Array.isArray(data.entries) ? data.entries : []).filter(e => e != null);
  if (entries.length === 0) return null;

  // Per-child inventory rides ALONGSIDE `entries` (never nested inside). The AUTHORITATIVE,
  // count-safe source is `inventoryLinesByChild` — the REAL matched line tickers per child, copied
  // verbatim from the backend preview; the card COUNTS `lines.length`. `childInventoryOverrides`
  // and the entries' scalar `lineCount` are only a legacy fallback (historical messages) — the
  // scalar was reproducibly fabricated by the model (it reported 1 regardless of the real count),
  // which is exactly the bug this replaces.
  const overrides = (Array.isArray(data.childInventoryOverrides)
    ? data.childInventoryOverrides
    : []
  ).filter(o => o != null);
  const linesByChild = (Array.isArray(data.inventoryLinesByChild)
    ? data.inventoryLinesByChild
    : []
  ).filter(l => l != null);

  // Pre-index both side arrays by ticker ONCE (see groupByTicker) instead of filtering them per
  // group inside the render loop below.
  const overridesByTicker = groupByTicker(overrides);
  const linesByChildByTicker = groupByTicker(linesByChild);

  const groupsByTicker = new Map<string, ChainUpdateEntry[]>();
  entries.forEach((e, i) => {
    const key = e.ticker ?? `#${i}`;
    groupsByTicker.set(key, [...(groupsByTicker.get(key) ?? []), e]);
  });
  const groups = Array.from(groupsByTicker.entries());

  return (
    <>
      {groups.map(([ticker, group]) => {
        const merged = mergedChainEntry(group, intl);
        const groupTicker = group[0]?.ticker ?? "";
        const inventoryByChild = buildPerChildInventory(
          merged.inventoryByChild,
          overridesByTicker.get(groupTicker) ?? [],
          linesByChildByTicker.get(groupTicker) ?? []
        );
        return (
          <ApprovalCard key={ticker}>
            <CardHeader title={ticker} />
            <PromotionChangeRows entry={{ ...merged, inventoryByChild }} intl={intl} />
            <ChainAffectedEngines entry={mergedChainScope(group)} intl={intl} />
          </ApprovalCard>
        );
      })}
    </>
  );
}
